import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Send, PhoneCall, Mail, MapPin } from "lucide-react";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const concerns = [
  "Immunity",
  "Stress Relief",
  "Digestive Care",
  "Joint Care",
  "Skin & Hair",
  "General Wellness",
  "Not sure — guide me",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    concern: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short note.");
      return;
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      toast.error("Email service isn't configured. Please try again later.");
      // eslint-disable-next-line no-console
      console.error(
        "EmailJS environment variables are missing. Check REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID and REACT_APP_EMAILJS_PUBLIC_KEY in your .env file."
      );
      return;
    }

    setLoading(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          concern: form.concern,
          message: form.message,
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      toast.success("Received. A wellness advisor will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", concern: "", message: "" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("EmailJS send failed:", err);
      toast.error("Something went wrong while sending your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#EBE4D8]/60 border-t border-[#DCD3C6]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <span className="overline">Consult</span>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
            Begin your
            <span className="italic text-[#C05C46]"> ritual.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[#5C6B5D]">
            Share a little about what you&apos;re carrying. A trained Ayurvedic
            advisor will read, listen, and write back with care — never a
            template.
          </p>

          <div className="mt-10 space-y-5">
            <ContactRow icon={<PhoneCall className="h-4 w-4" strokeWidth={1.5} />} label="+91 98 3216 5040" />
            <ContactRow icon={<Mail className="h-4 w-4" strokeWidth={1.5} />} label="ayurvedauniversal4@gmail.com" />
            <ContactRow icon={<MapPin className="h-4 w-4" strokeWidth={1.5} />} label="Rishikesh · Bengaluru · London" />
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="md:col-span-7 bg-[#F8F5F0] border border-[#DCD3C6] rounded-3xl p-8 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Your name" required>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Ranjana Sharma"
                className={inputClass}
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@example.com"
                className={inputClass}
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 …"
                className={inputClass}
              />
            </Field>
            <Field label="Primary concern">
              <select
                data-testid="contact-input-concern"
                value={form.concern}
                onChange={update("concern")}
                className={inputClass}
              >
                <option value="">Choose one…</option>
                {concerns.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Tell us more" required className="mt-6">
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={update("message")}
              rows={5}
              placeholder="A few lines about how you feel, sleep, digest — anything that matters."
              className={`${inputClass} resize-none`}
            />
          </Field>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[#5C6B5D] max-w-sm">
              By sending this, you agree to be contacted by an Ayurvedauniversal advisor. We never share your details.
            </p>
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Message"}
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

const inputClass =
  "w-full bg-transparent border-b border-[#DCD3C6] focus:border-[#C05C46] outline-none py-3 text-[#2E3A2F] placeholder:text-[#5C6B5D]/60 transition-colors";

function Field({ label, children, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline">
        {label} {required && <span className="text-[#C05C46]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function ContactRow({ icon, label }) {
  return (
    <div className="flex items-center gap-4">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#DCD3C6] text-[#2E3A2F]">
        {icon}
      </span>
      <span className="text-[#2E3A2F]">{label}</span>
    </div>
  );
}
